'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Sparkles, Zap, Heart, Smile, Shield, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useRTL } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);

export default function WhatAreVeneersSection() {
    const { t } = useTranslation('dental_veneers');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);

    const keyApplications = [
        {
            icon: Sparkles,
            title: t('whatAre.applications.concealStains.title'),
            description: t('whatAre.applications.concealStains.description'),
        },
        {
            icon: Zap,
            title: t('whatAre.applications.fixChips.title'),
            description: t('whatAre.applications.fixChips.description'),
        },
        {
            icon: Smile,
            title: t('whatAre.applications.closeGaps.title'),
            description: t('whatAre.applications.closeGaps.description'),
        },
        {
            icon: Heart,
            title: t('whatAre.applications.correctUneven.title'),
            description: t('whatAre.applications.correctUneven.description'),
        },
        {
            icon: Shield,
            title: t('whatAre.applications.longLasting.title'),
            description: t('whatAre.applications.longLasting.description'),
        },
        {
            icon: Clock,
            title: t('whatAre.applications.minimallyInvasive.title'),
            description: t('whatAre.applications.minimallyInvasive.description'),
        }
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states
            gsap.set([titleRef.current, contentRef.current], {
                opacity: 0,
                y: 50
            });

            if (cardsRef.current?.children) {
                gsap.set(cardsRef.current.children, {
                    opacity: 0,
                    y: 50,
                    scale: 0.9
                });
            }

            // Title animation with scroll trigger
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Content animation
            gsap.to(contentRef.current, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: contentRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Individual card animations - each card appears based on its own scroll position
            if (cardsRef.current?.children) {
                Array.from(cardsRef.current.children).forEach((card, index) => {
                    gsap.to(card, {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: card,
                            start: "top 85%", // Card becomes visible when its top hits 85% of viewport
                            end: "bottom 15%",
                            toggleActions: "play none none reverse",
                            scrub: false
                        }
                    });
                });
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="what-are"
            ref={sectionRef}
            className="py-20 bg-slate-50 dark:bg-navy-950 relative overflow-hidden"
        >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 w-32 h-32 bg-gold rounded-full blur-2xl"></div>
                <div className="absolute bottom-20 right-20 w-40 h-40 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gold/20 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className={cn(
                            "text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif",
                            isRTL && "font-arabic"
                        )}
                    >
                        {t('whatAre.title.part1')}{' '}
                        <span className="text-gold relative">
                            {t('whatAre.title.part2')}
                            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gold rounded-full"></div>
                        </span>
                    </h2>

                    <div ref={contentRef} className="space-y-6">
                        <p className={cn(
                            "text-xl md:text-2xl text-slate-600 dark:text-slate-300 leading-relaxed",
                            isRTL && "font-arabic"
                        )}>
                            {t('whatAre.description')}
                        </p>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                            viewport={{ once: true }}
                            className={cn(
                                "inline-flex items-center bg-gold/10 text-gold dark:text-gold px-6 py-3 rounded-full text-lg font-medium border border-gold/20 gap-3",
                                isRTL && "flex-row-reverse font-arabic"
                            )}
                        >
                            <Sparkles className="w-6 h-6" />
                            <span>{t('whatAre.ctaButton')}</span>
                            <Sparkles className="w-6 h-6" />
                        </motion.div>
                    </div>
                </div>

                {/* Key Applications Grid */}
                <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {keyApplications.map((application, index) => (
                        <motion.div
                            key={index}
                            whileHover={{
                                scale: 1.05,
                                y: -10
                            }}
                            transition={{ duration: 0.3 }}
                            className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-200 dark:border-slate-700 group relative overflow-hidden"
                        >
                            {/* Background Gradient */}
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl bg-gradient-to-br from-gold/20 to-gold/5"></div>

                            <div className="relative z-10 flex flex-col items-center text-center">
                                {/* Icon */}
                                <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 bg-gold/20 border-2 border-gold/30">
                                    <application.icon className="w-8 h-8 text-gold transition-colors duration-300" />
                                </div>

                                {/* Content */}
                                <h3 className={cn(
                                    "text-xl font-bold text-navy dark:text-white mb-4 group-hover:text-gold dark:group-hover:text-gold transition-colors duration-300",
                                    isRTL && "font-arabic"
                                )}>
                                    {application.title}
                                </h3>

                                <p className={cn(
                                    "text-slate-600 dark:text-slate-300 leading-relaxed",
                                    isRTL && "font-arabic"
                                )}>
                                    {application.description}
                                </p>

                                {/* Decorative Element */}
                                <div className="absolute bottom-4 end-4 w-12 h-12 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-300 bg-[radial-gradient(circle,_theme(colors.gold/0.4),_transparent)]"></div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mt-16"
                >
                    <div className="bg-gold/10 rounded-2xl p-8 border border-gold/20 max-w-3xl mx-auto">
                        <h3 className={cn(
                            "text-2xl font-bold text-navy dark:text-white mb-4",
                            isRTL && "font-arabic"
                        )}>
                            {t('whatAre.cta.title')}
                        </h3>
                        <p className={cn(
                            "text-slate-600 dark:text-slate-300 text-lg",
                            isRTL && "font-arabic"
                        )}>
                            {t('whatAre.cta.description')}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
} 
